import React, { useEffect, useRef } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Typography, Skeleton, Stack,
  useTheme, useMediaQuery
} from '@mui/material';
import { InboxOutlined } from '@mui/icons-material';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import useProTableState from './useProTableState';
import useColumnPreferences from './useColumnPreferences';
import ProTableFilters from './ProTableFilters';
import ProTablePreferences from './ProTablePreferences';
import ProTableStats from './ProTableStats';

const TABLE_CELL_SX = {
  fontSize: '0.8125rem',
  py: 0.75,
  lineHeight: 1.4,
};
const TABLE_HEAD_CELL_SX = {
  fontSize: '0.75rem',
  fontWeight: 600,
  py: 1,
  lineHeight: 1.4,
};

const ProTable = ({
  tableKey,
  columns,
  fetchData,
  filters: filterDefs = [],
  statsCards,
  actions,
  onRowClick,
  rowActions,
  defaultPageSize = 20,
  defaultDateRange = '30days',
  summaryRow,
  countLabel = 'records',
  rowRenderMobile,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const state = useProTableState({ fetchData, filters: filterDefs, defaultPageSize, defaultDateRange });
  const colPrefs = useColumnPreferences(tableKey, columns);
  const statsShown = useRef(false);
  useEffect(() => {
    if (statsCards && !state.loading) statsShown.current = true;
  });

  const visibleCols = colPrefs.visibleColumns;
  const allVisibleCols = rowActions
    ? [...visibleCols, { key: '__actions', label: 'Actions', align: 'right', width: 120 }]
    : visibleCols;

  return (
    <Box>
      {/* Stats Cards */}
      {statsCards && <ProTableStats cards={statsCards} loading={state.loading && !statsShown.current} />}

      {/* Filters + Actions row */}
      <ProTableFilters filters={filterDefs} state={state} actions={actions} />

      {/* Count + Preferences row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75, mt: 0.5, px: 0.5 }}>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {state.total} {countLabel}
        </Typography>
        <ProTablePreferences columns={columns} preferences={colPrefs} />
      </Box>

      {/* Mobile Card View */}
      {isMobile && rowRenderMobile ? (
        <Stack spacing={1.5}>
          {state.loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography color="text.secondary">Loading...</Typography>
            </Box>
          ) : state.data.length === 0 ? (
            <Paper sx={{ py: 4, textAlign: 'center' }}>
              <InboxOutlined sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                No {countLabel} found
              </Typography>
            </Paper>
          ) : (
            state.data.map((row, i) => (
              <Box key={row.id || i} onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}>
                {rowRenderMobile(row)}
              </Box>
            ))
          )}
        </Stack>
      ) : (
        /* Desktop Table */
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={getBrandedTableHeadSx(theme)}>
                  {allVisibleCols.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align || 'left'}
                      sx={{ ...TABLE_HEAD_CELL_SX, width: col.width }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {state.loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {allVisibleCols.map((col) => (
                        <TableCell key={col.key} sx={TABLE_CELL_SX}>
                          <Skeleton />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : state.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={allVisibleCols.length} align="center" sx={{ py: 6 }}>
                      <InboxOutlined sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        No {countLabel} found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {state.data.map((row, i) => (
                      <TableRow
                        key={row.id || i}
                        hover
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                        sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                      >
                        {visibleCols.map((col) => (
                          <TableCell key={col.key} align={col.align || 'left'} sx={TABLE_CELL_SX}>
                            {col.render ? col.render(row) : row[col.key]}
                          </TableCell>
                        ))}
                        {rowActions && (
                          <TableCell align="right" sx={{ ...TABLE_CELL_SX, width: 120 }}>
                            {rowActions(row)}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {summaryRow && (
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        {allVisibleCols.map((col, i) => (
                          <TableCell key={col.key} align={col.align || 'left'}
                            sx={{ ...TABLE_CELL_SX, fontWeight: 600 }}>
                            {i === 0 ? summaryRow.label : (summaryRow.values?.[col.key] ?? '')}
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={state.total}
            page={state.page}
            onPageChange={(_, p) => state.setPage(p)}
            rowsPerPage={state.pageSize}
            onRowsPerPageChange={(e) => state.setPageSize(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 20, 50, 100]}
            sx={{
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: '0.75rem',
              },
            }}
          />
        </Paper>
      )}

      {/* Error display */}
      {state.error && (
        <Typography color="error" sx={{ fontSize: '0.8125rem', mt: 1 }}>
          {state.error}
        </Typography>
      )}
    </Box>
  );
};

export default ProTable;
